/**
 * Reduced Phase 1 — Performance Trend chart.
 * Client-side aggregation of published reports into weekly buckets.
 * Distinguishes own vs competitor rows purely via branch metadata that
 * the caller has already resolved. Uses TrustX® score as the numeric
 * signal (this is what /api/reports currently ships alongside each row).
 */
import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from 'recharts';

function toWeekBucket(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10));
  if (Number.isNaN(d.getTime())) return null;
  // ISO-ish week key: YYYY-Wxx (Mon-based). Simple enough for a 90-day window.
  const day = (d.getDay() + 6) % 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - day);
  const yyyy = monday.getFullYear();
  const mm = String(monday.getMonth() + 1).padStart(2, '0');
  const dd = String(monday.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function mean(vals) {
  const clean = vals.filter(v => typeof v === 'number' && !Number.isNaN(v));
  if (clean.length === 0) return null;
  return Math.round((clean.reduce((s, v) => s + v, 0) / clean.length) * 10) / 10;
}

export function PerformanceTrend({ reports, competitorBranchIds }) {
  const compSet = useMemo(() => new Set(competitorBranchIds || []), [competitorBranchIds]);

  const data = useMemo(() => {
    const buckets = new Map(); // week -> { own: [], comp: [] }
    (reports || []).forEach(r => {
      const score = r.trustx_overall;
      if (score == null) return;
      const wk = toWeekBucket(r.visit_date);
      if (!wk) return;
      if (!buckets.has(wk)) buckets.set(wk, { own: [], comp: [] });
      const isComp = r.branch_id && compSet.has(r.branch_id);
      buckets.get(wk)[isComp ? 'comp' : 'own'].push(score);
    });
    const rows = Array.from(buckets.entries())
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([wk, { own, comp }]) => ({
        week: wk.slice(5), // MM-DD is sufficient tick
        own: mean(own),
        competitor: mean(comp),
      }));
    return rows;
  }, [reports, compSet]);

  if (data.length === 0) {
    return (
      <div
        className="bg-white border border-[#E2E8F0] rounded-xl p-6 text-sm text-[#94A3B8]"
        data-testid="ms-trend-empty"
      >
        Not enough data yet to plot a trend. Trend appears once at least one
        weekly bucket has a scored report.
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-xl p-4"
      data-testid="ms-trend-chart"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-[#0F172A]">Performance Trend (weekly TrustX®)</h3>
        <span className="text-[11px] text-[#94A3B8]">{data.length} week{data.length === 1 ? '' : 's'}</span>
      </div>
      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748B' }} />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 11, fill: '#64748B' }}
              tickFormatter={(v) => `${v}%`}
              width={44}
            />
            <Tooltip
              formatter={(v) => (v == null ? '—' : `${v}%`)}
              contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: '#E2E8F0' }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line
              type="monotone"
              dataKey="own"
              name="Own"
              stroke="#1A3E6F"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="competitor"
              name="Competitor"
              stroke="#F59E0B"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default PerformanceTrend;
